
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, Plus, Mail, UserCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { UserRole } from "../contexts/AuthContext";
import { toast } from "sonner";
import { getRoleAvatarData } from "@/lib/avatarUtils";

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: "active" | "pending";
  taskCompletion: number;
  workHours: number;
}

const Users = () => {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  
  // Mock users data
  useEffect(() => {
    const mockUsers: User[] = [
      {
        id: "1",
        name: "Sarah Johnson",
        email: "parent@example.com",
        role: "parent",
        status: "active",
        taskCompletion: 85,
        workHours: 18
      },
      {
        id: "2",
        name: "Michael Chen",
        email: "teacher@example.com",
        role: "teacher",
        status: "active",
        taskCompletion: 92,
        workHours: 40
      },
      {
        id: "3",
        name: "Emily Rodriguez",
        email: "board@example.com",
        role: "board",
        status: "active",
        taskCompletion: 95,
        workHours: 25
      },
      {
        id: "4",
        name: "David Kim",
        email: "david.kim@example.com",
        role: "parent",
        status: "active",
        taskCompletion: 65,
        workHours: 15
      },
      {
        id: "5",
        name: "Maria Wong",
        email: "maria.wong@example.com",
        role: "parent",
        status: "active",
        taskCompletion: 78,
        workHours: 20
      },
      {
        id: "6",
        name: "James Taylor",
        email: "james.taylor@example.com",
        role: "parent",
        status: "active",
        taskCompletion: 90,
        workHours: 22
      },
      {
        id: "7",
        name: "Lisa Garcia",
        email: "lisa.garcia@example.com",
        role: "teacher",
        status: "active",
        taskCompletion: 88,
        workHours: 38
      },
      {
        id: "8",
        name: "Robert Wilson",
        email: "robert.wilson@example.com",
        role: "parent",
        status: "pending",
        taskCompletion: 0,
        workHours: 0
      },
    ];
    
    setUsers(mockUsers);
  }, []);

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Filter by role
  const boardMembers = filteredUsers.filter((user) => user.role === "board");
  const teachers = filteredUsers.filter((user) => user.role === "teacher");
  const parents = filteredUsers.filter((user) => user.role === "parent");
  const pendingUsers = filteredUsers.filter((user) => user.status === "pending");

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case "parent":
        return "bg-secondary text-secondary-foreground";
      case "teacher":
        return "bg-accent text-accent-foreground";
      case "board":
        return "bg-primary text-primary-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const handleApproveUser = (userId: string) => {
    // In a real app, this would make an API request
    setUsers(
      users.map((user) =>
        user.id === userId ? { ...user, status: "active" } : user
      )
    );
    toast.success("User approved successfully");
  };
  
  const handleEmailUser = (email: string) => {
    // In a real app, this would open an email compose window or modal
    toast.info(`Email ${email}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Users</h1>
        <Button>
          <Plus className="h-4 w-4 mr-1" />
          Add User
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search users..."
          className="pl-8 w-full md:max-w-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All Users ({filteredUsers.length})</TabsTrigger>
          <TabsTrigger value="parents">Parents ({parents.length})</TabsTrigger>
          <TabsTrigger value="teachers">Teachers ({teachers.length})</TabsTrigger>
          <TabsTrigger value="board">Board ({boardMembers.length})</TabsTrigger>
          {pendingUsers.length > 0 && (
            <TabsTrigger value="pending">
              Pending ({pendingUsers.length})
            </TabsTrigger>
          )}
        </TabsList>
        
        <div className="mt-6">
          <TabsContent value="all" className="m-0">
            <UsersList users={filteredUsers} onEmailUser={handleEmailUser} onApproveUser={handleApproveUser} />
          </TabsContent>
          <TabsContent value="parents" className="m-0">
            <UsersList users={parents} onEmailUser={handleEmailUser} onApproveUser={handleApproveUser} />
          </TabsContent>
          <TabsContent value="teachers" className="m-0">
            <UsersList users={teachers} onEmailUser={handleEmailUser} onApproveUser={handleApproveUser} />
          </TabsContent>
          <TabsContent value="board" className="m-0">
            <UsersList users={boardMembers} onEmailUser={handleEmailUser} onApproveUser={handleApproveUser} />
          </TabsContent>
          <TabsContent value="pending" className="m-0">
            <UsersList users={pendingUsers} onEmailUser={handleEmailUser} onApproveUser={handleApproveUser} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

interface UsersListProps {
  users: User[];
  onEmailUser: (email: string) => void;
  onApproveUser: (userId: string) => void;
}

const UsersList = ({ users, onEmailUser, onApproveUser }: UsersListProps) => {
  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case "parent":
        return "bg-secondary/20 text-secondary-foreground border-secondary/20";
      case "teacher":
        return "bg-accent/20 text-accent-foreground border-accent/20";
      case "board":
        return "bg-primary/20 text-primary-foreground border-primary/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (users.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No users found.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {users.map((user) => (
        <Card key={user.id} className="overflow-hidden">
          <CardContent className="p-0">
            <div className="p-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback 
                    className={getRoleAvatarData(user.role, user.name, user.email).backgroundColor}
                  >
                    {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-medium">{user.name}</h3>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Badge
                  variant="outline"
                  className={`${getRoleBadgeClass(user.role)} capitalize`}
                >
                  {user.role}
                </Badge>
                {user.status === "pending" ? (
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">
                    Pending Approval
                  </Badge>
                ) : null}
              </div>
              {user.status === "active" && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-muted-foreground">Task Completion</div>
                    <div className="font-medium">{user.taskCompletion}%</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Work Hours</div>
                    <div className="font-medium">{user.workHours} hrs</div>
                  </div>
                </div>
              )}
            </div>
            <div className="border-t flex divide-x">
              <Button
                variant="ghost"
                className="flex-1 rounded-none h-10"
                onClick={() => onEmailUser(user.email)}
              >
                <Mail className="h-4 w-4 mr-1" />
                Email
              </Button>
              {user.status === "pending" && (
                <Button
                  variant="ghost"
                  className="flex-1 rounded-none h-10"
                  onClick={() => onApproveUser(user.id)}
                >
                  <UserCheck className="h-4 w-4 mr-1" />
                  Approve
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default Users;
